import { useState, type FC } from 'react';
import CountDownCore from 'react-countdown';
import styled from 'styled-components';
import { useIsMounted } from '~/common/hooks/useIsMounted';
import {
  COUNTDOWN_DIGITS_MIN_HEIGHT,
  CountdownTitle,
  countdownPositioning,
  countdownRenderer,
} from '~/components/countdown';

interface PageCountdownProps {
  title: string | null;
  /** Epoch ms. `countdown.server.ts` has already rejected unparseable and past values. */
  at: number | null;
}

/**
 * The card-screen countdown, on every surface that shows one: the private sale's
 * sign-up page, password gates and product list, plus `/signup` — which reads the
 * site-wide `COUNTDOWN_*` pair rather than the private sale's, so two campaigns share
 * this component but never a value.
 *
 * Not `/shop`'s `Countdown`, which is a `<Link to={Routes.SignUp}>` — on these pages
 * that would route private-sale traffic into the *newsletter* sign-up — and which
 * self-configured from `window.ENV` where this takes props. The title and digit styles
 * are IMPORTED from the shared presentation module, so "same design as /shop" cannot
 * drift.
 *
 * **The page owns the header clearance, not this component**, exactly as on `/shop`:
 * in flow it has `padding-top: 0`, and the caller supplies the offset (`PageWrapper`'s
 * `paddingTop` on the product list, an explicit rule on the gate screens). A variant
 * prop here would have to know which, and it would double to 9rem the moment someone
 * used the wrong one.
 *
 * Digits render only after mount. `react-countdown` computes its first delta from
 * `Date.now()` in its constructor, so a server-rendered second differs from the
 * hydrated one and React logs a text mismatch. The title has no time dependency and is
 * server-rendered normally.
 */
export const PageCountdown: FC<PageCountdownProps> = ({ title, at }) => {
  const { isMounted } = useIsMounted();
  const [completed, setCompleted] = useState(false);

  const showDigits = at !== null && !completed;

  // Nothing configured, or the timer ran out and there is no title to keep. Unlike
  // `/shop`, this leaves no empty box behind holding layout open.
  if (!title && !showDigits) return null;

  return (
    <Container role="timer">
      {title && <CountdownTitle>{title}</CountdownTitle>}
      {showDigits && (
        <DigitsSlot>
          {isMounted && (
            <CountDownCore
              date={at}
              renderer={countdownRenderer}
              onComplete={() => setCompleted(true)}
            />
          )}
        </DigitsSlot>
      )}
    </Container>
  );
};

const Container = styled.div`
  ${countdownPositioning}
`;

// Reserved so the page does not jump when the digits appear after mount.
const DigitsSlot = styled.div`
  min-height: ${COUNTDOWN_DIGITS_MIN_HEIGHT};
`;
